import type {
  ContentType,
  CreateContentTypeInput,
  UpdateContentTypeInput,
  Entry,
  CreateEntryInput,
  UpdateEntryInput,
  MediaAsset,
  PresignInput,
  PresignResponse,
  Product,
  CreateProductInput,
  UpdateProductInput,
  AdjustStockInput,
  StockMovement,
  SquareSettingsStatus,
  UpdateSquareSettingsInput,
  StripeSettingsStatus,
  UpdateStripeSettingsInput,
  SiteSettingsStatus,
  UpdateSiteSettingsInput,
  Subscriber,
} from "@eaub/shared";

/** Error thrown for any failed API call. Carries the server error code/status. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(message: string, status: number, code: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

type Method = "GET" | "POST" | "PUT" | "DELETE";

/** Shape of the standard error envelope returned by the API. */
function toApiError(body: unknown, status: number): ApiError {
  if (typeof body === "object" && body !== null && "error" in body) {
    const err = (body as { error?: unknown }).error;
    if (typeof err === "object" && err !== null) {
      const e = err as { code?: unknown; message?: unknown; details?: unknown };
      const message = typeof e.message === "string" ? e.message : `Request failed (${status})`;
      const code = typeof e.code === "string" ? e.code : "unknown_error";
      return new ApiError(message, status, code, e.details);
    }
  }
  return new ApiError(`Request failed (${status})`, status, "unknown_error");
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

export interface ApiClient {
  // Content types
  listContentTypes(): Promise<ContentType[]>;
  createContentType(input: CreateContentTypeInput): Promise<ContentType>;
  getContentType(id: string): Promise<ContentType>;
  updateContentType(id: string, input: UpdateContentTypeInput): Promise<ContentType>;
  deleteContentType(id: string): Promise<void>;

  // Entries
  listEntries(typeId: string): Promise<Entry[]>;
  createEntry(typeId: string, input: CreateEntryInput): Promise<Entry>;
  getEntry(id: string): Promise<Entry>;
  updateEntry(id: string, input: UpdateEntryInput): Promise<Entry>;
  deleteEntry(id: string): Promise<void>;
  publishEntry(id: string): Promise<Entry>;
  unpublishEntry(id: string): Promise<Entry>;
  previewEntry(id: string): Promise<string>;

  // Media
  listMedia(): Promise<MediaAsset[]>;
  presignMedia(input: PresignInput): Promise<PresignResponse>;

  // Products / inventory
  listProducts(): Promise<Product[]>;
  createProduct(input: CreateProductInput): Promise<Product>;
  getProduct(id: string): Promise<Product>;
  updateProduct(id: string, input: UpdateProductInput): Promise<Product>;
  deleteProduct(id: string): Promise<void>;
  adjustStock(id: string, input: AdjustStockInput): Promise<{ product: Product; movement: StockMovement }>;
  listMovements(id: string): Promise<StockMovement[]>;
  publishProduct(id: string): Promise<Product>;
  unpublishProduct(id: string): Promise<Product>;

  // Integrations
  getSiteSettings(): Promise<SiteSettingsStatus>;
  updateSiteSettings(input: UpdateSiteSettingsInput): Promise<SiteSettingsStatus>;
  getSquareSettings(): Promise<SquareSettingsStatus>;
  updateSquareSettings(input: UpdateSquareSettingsInput): Promise<SquareSettingsStatus>;
  syncAllToSquare(): Promise<void>;
  getStripeSettings(): Promise<StripeSettingsStatus>;
  updateStripeSettings(input: UpdateStripeSettingsInput): Promise<StripeSettingsStatus>;

  // Mailing list
  listSubscribers(): Promise<Subscriber[]>;
  deleteSubscriber(id: string): Promise<void>;

  // Site
  publishSite(): Promise<void>;
}

/**
 * Read a current Cognito access token before each authenticated API request.
 */
export function createApiClient(baseUrl: string, getToken: () => Promise<string>, onUnauthorized?: () => void): ApiClient {
  const root = baseUrl.replace(/\/+$/, "");

  async function request<T>(method: Method, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { Accept: "application/json" };
    const hasBody = body !== undefined;
    if (hasBody) headers["Content-Type"] = "application/json";
    const token = await getToken();
    headers.Authorization = `Bearer ${token}`;

    let response: Response;
    try {
      response = await fetch(`${root}${path}`, {
        method,
        headers,
        body: hasBody ? JSON.stringify(body) : undefined,
      });
    } catch (cause) {
      throw new ApiError("Network error — could not reach the API.", 0, "network_error", cause);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    if (response.status === 401) onUnauthorized?.();
    const parsed = await readBody(response);
    if (!response.ok) {
      throw toApiError(parsed, response.status);
    }
    return parsed as T;
  }

  return {
    async listContentTypes() {
      const { items } = await request<{ items: ContentType[] }>("GET", "/content-types");
      return items;
    },
    async createContentType(input) {
      const { item } = await request<{ item: ContentType }>("POST", "/content-types", input);
      return item;
    },
    async getContentType(id) {
      const { item } = await request<{ item: ContentType }>(
        "GET",
        `/content-types/${encodeURIComponent(id)}`,
      );
      return item;
    },
    async updateContentType(id, input) {
      const { item } = await request<{ item: ContentType }>(
        "PUT",
        `/content-types/${encodeURIComponent(id)}`,
        input,
      );
      return item;
    },
    async deleteContentType(id) {
      await request<void>("DELETE", `/content-types/${encodeURIComponent(id)}`);
    },

    async listEntries(typeId) {
      const { items } = await request<{ items: Entry[] }>(
        "GET",
        `/content-types/${encodeURIComponent(typeId)}/entries`,
      );
      return items;
    },
    async createEntry(typeId, input) {
      const { item } = await request<{ item: Entry }>(
        "POST",
        `/content-types/${encodeURIComponent(typeId)}/entries`,
        input,
      );
      return item;
    },
    async getEntry(id) {
      const { item } = await request<{ item: Entry }>("GET", `/entries/${encodeURIComponent(id)}`);
      return item;
    },
    async updateEntry(id, input) {
      const { item } = await request<{ item: Entry }>(
        "PUT",
        `/entries/${encodeURIComponent(id)}`,
        input,
      );
      return item;
    },
    async deleteEntry(id) {
      await request<void>("DELETE", `/entries/${encodeURIComponent(id)}`);
    },
    async publishEntry(id) {
      const { item } = await request<{ item: Entry }>(
        "POST",
        `/entries/${encodeURIComponent(id)}/publish`,
      );
      return item;
    },
    async unpublishEntry(id) {
      const { item } = await request<{ item: Entry }>(
        "POST",
        `/entries/${encodeURIComponent(id)}/unpublish`,
      );
      return item;
    },
    async previewEntry(id) {
      const { html } = await request<{ html: string }>(
        "POST",
        `/entries/${encodeURIComponent(id)}/preview`,
      );
      return html;
    },

    async listMedia() {
      const { items } = await request<{ items: MediaAsset[] }>("GET", "/media");
      return items;
    },
    async presignMedia(input) {
      return request<PresignResponse>("POST", "/media/presign", input);
    },

    async listProducts() {
      const { items } = await request<{ items: Product[] }>("GET", "/products");
      return items;
    },
    async createProduct(input) {
      const { item } = await request<{ item: Product }>("POST", "/products", input);
      return item;
    },
    async getProduct(id) {
      const { item } = await request<{ item: Product }>(
        "GET",
        `/products/${encodeURIComponent(id)}`,
      );
      return item;
    },
    async updateProduct(id, input) {
      const { item } = await request<{ item: Product }>(
        "PUT",
        `/products/${encodeURIComponent(id)}`,
        input,
      );
      return item;
    },
    async deleteProduct(id) {
      await request<void>("DELETE", `/products/${encodeURIComponent(id)}`);
    },
    async adjustStock(id, input) {
      const { item, movement } = await request<{ item: Product; movement: StockMovement }>(
        "POST",
        `/products/${encodeURIComponent(id)}/stock`,
        input,
      );
      return { product: item, movement };
    },
    async listMovements(id) {
      const { items } = await request<{ items: StockMovement[] }>(
        "GET",
        `/products/${encodeURIComponent(id)}/movements`,
      );
      return items;
    },
    async publishProduct(id) {
      const { item } = await request<{ item: Product }>(
        "POST",
        `/products/${encodeURIComponent(id)}/publish`,
      );
      return item;
    },
    async unpublishProduct(id) {
      const { item } = await request<{ item: Product }>(
        "POST",
        `/products/${encodeURIComponent(id)}/unpublish`,
      );
      return item;
    },

    async getSiteSettings() {
      const { item } = await request<{ item: SiteSettingsStatus }>("GET", "/integrations/mode");
      return item;
    },
    async updateSiteSettings(input) {
      const { item } = await request<{ item: SiteSettingsStatus }>(
        "PUT",
        "/integrations/mode",
        input,
      );
      return item;
    },
    async getSquareSettings() {
      const { item } = await request<{ item: SquareSettingsStatus }>(
        "GET",
        "/integrations/square",
      );
      return item;
    },
    async updateSquareSettings(input) {
      const { item } = await request<{ item: SquareSettingsStatus }>(
        "PUT",
        "/integrations/square",
        input,
      );
      return item;
    },
    async syncAllToSquare() {
      await request<{ ok: true }>("POST", "/integrations/square/sync-all");
    },
    async getStripeSettings() {
      const { item } = await request<{ item: StripeSettingsStatus }>(
        "GET",
        "/integrations/stripe",
      );
      return item;
    },
    async updateStripeSettings(input) {
      const { item } = await request<{ item: StripeSettingsStatus }>(
        "PUT",
        "/integrations/stripe",
        input,
      );
      return item;
    },

    async listSubscribers() {
      const { items } = await request<{ items: Subscriber[] }>("GET", "/subscribers");
      return items;
    },
    async deleteSubscriber(id) {
      await request<void>("DELETE", `/subscribers/${encodeURIComponent(id)}`);
    },

    async publishSite() {
      await request<{ ok: true }>("POST", "/publish");
    },
  };
}

/**
 * Upload a file directly to S3 using a presigned PUT URL.
 * No Authorization header: auth is embedded in the signed URL. The Content-Type
 * MUST match the value sent to the presign endpoint.
 */
export async function putToPresignedUrl(
  uploadUrl: string,
  file: Blob,
  contentType: string,
): Promise<void> {
  let response: Response;
  try {
    response = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": contentType },
      body: file,
    });
  } catch (cause) {
    throw new ApiError("Network error during upload.", 0, "upload_network_error", cause);
  }
  if (!response.ok) {
    throw new ApiError(`Upload failed (HTTP ${response.status})`, response.status, "upload_failed");
  }
}
